import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PhoneCall, ChevronLeft, CheckCircle2, RotateCcw, BookUser } from 'lucide-react';
import DwShell from '../components/home/dw/DwShell';
import { useAuth } from '../contexts/AuthContext';
import { CallingSetup } from '../components/calling/CallingSetup';
import { CallCard } from '../components/calling/CallCard';
import { QueueList } from '../components/calling/QueueList';
import { SidePanel } from '../components/calling/SidePanel';
import { StatsStrip, CallbacksPanel, ResumeList } from '../components/calling/Lobby';
import { DailyCalls } from '../components/calling/DailyCalls';
import { CARD, Btn, cx } from '../components/contacts/ui';

const API = process.env.REACT_APP_BACKEND_URL;
const warnBooked = (d) => (d?.just_booked || []).forEach((n) => toast.info(`${n} just booked an appointment in GoHighLevel, so they've been skipped`));

const Done = ({ session, onRequeue, onNew }) => (
  <section className={cx(CARD, 'p-8 text-center')} data-testid="call-session-done">
    <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--green)]" />
    <h2 className="dw-ink mt-3 text-2xl font-bold tracking-tight">Session {session.status === 'finished' ? 'finished' : 'complete'}</h2>
    <p className="mt-1 text-sm text-[var(--muted)]">{session.done} of {session.total} called{session.skipped ? ` · ${session.skipped} skipped` : ''}. Every call is saved to each contact's timeline.</p>
    <div className="mt-5 flex flex-wrap justify-center gap-2">
      {!!session.skipped && <Btn tone="solid" onClick={onRequeue} data-testid="call-requeue-skipped"><RotateCcw className="h-4 w-4" />Call the {session.skipped} skipped</Btn>}
      <Btn tone="ghost" onClick={onNew} data-testid="call-new-session">Start a new queue</Btn>
    </div>
  </section>
);

export default function CallingQueue() {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const sid = params.get('session');
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [callbacks, setCallbacks] = useState([]);
  const [sessions, setSessions] = useState([]);

  const refreshLobby = useCallback(() => {
    axios.get(`${API}/api/calling/stats`, { headers }).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/api/calling/callbacks`, { headers }).then((r) => setCallbacks(r.data.items || [])).catch(() => {});
    axios.get(`${API}/api/calling/sessions`, { headers }).then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  }, [headers]);
  useEffect(() => { refreshLobby(); }, [refreshLobby, sid]);

  const load = useCallback(() => {
    if (!sid) { setSession(null); return; }
    axios.get(`${API}/api/calling/sessions/${sid}`, { headers }).then((r) => { setSession(r.data); warnBooked(r.data); }).catch(() => setParams({}));
  }, [sid, headers, setParams]);
  useEffect(() => { load(); if (!sid) return undefined; const t = setInterval(load, 8000); return () => clearInterval(t); }, [load, sid]);

  const open = (s) => { setSession(s); setParams({ session: s.id }); };
  const post = async (path, body, msg) => {
    setBusy(true);
    try { const r = await axios.post(`${API}/api/calling/sessions/${sid}/${path}`, body || {}, { headers }); setSession(r.data); warnBooked(r.data); if (msg) toast.success(msg); return r.data; }
    catch (e) { toast.error(e.response?.data?.detail || 'Something went wrong'); return null; }
    finally { setBusy(false); }
  };
  const resume = async (id) => { const r = await axios.post(`${API}/api/calling/sessions/${id}/resume`, {}, { headers }); open(r.data); };
  const startCallbacks = async () => { try { const r = await axios.post(`${API}/api/calling/sessions`, { source: 'callbacks' }, { headers }); open(r.data); } catch (e) { toast.error(e.response?.data?.detail || 'No callbacks due today'); } };
  const cbDone = async (c) => { await axios.post(`${API}/api/calling/callbacks/${c.id}/done`, {}, { headers }); toast.success('Callback cleared'); refreshLobby(); };
  const cbMove = async (c, due) => { await axios.post(`${API}/api/calling/callbacks/${c.id}/reschedule`, { due_at: due }, { headers }); toast.success('Callback moved'); refreshLobby(); };

  const item = session && session.items[session.cursor];
  const finished = session && (session.status === 'finished' || session.remaining === 0);
  const dueToday = callbacks.filter((c) => new Date(c.due_at) <= new Date(Date.now() + 14 * 3600e3)).length;

  return (
    <DwShell activeKey="client-portal">
      <div className="mx-auto max-w-[1400px] px-3 pb-36 pt-3 sm:px-6 sm:py-8" data-testid="calling-page">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => (sid ? setParams({}) : nav('/onboarding'))} className="rounded-full p-1.5 text-[var(--muted)] hover:bg-[var(--hover)]" data-testid="calling-back"><ChevronLeft className="h-5 w-5" /></button>
            <div>
              <p className="font-[var(--mono)] text-[10px] font-bold uppercase tracking-[.14em] text-[var(--faint)]">Outbound</p>
              <h1 className="dw-title dw-ink flex items-center gap-2 text-2xl font-bold tracking-tight text-[var(--ink)] sm:text-3xl"><PhoneCall className="h-6 w-6 text-[var(--green)]" />Calling queue</h1>
            </div>
          </div>
          <Link to="/contacts" data-testid="calling-open-contacts" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-[var(--surface)] px-4 py-2 text-sm font-semibold text-[var(--ink-2)] hover:bg-[var(--hover)]"><BookUser className="h-4 w-4" />Contacts</Link>
        </div>
        <div className={sid ? 'hidden lg:block' : ''}><StatsStrip stats={stats} /></div>

        {!sid && (
          <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
            <div className="space-y-4"><DailyCalls headers={headers} onStarted={open} /><CallingSetup headers={headers} onStarted={open} me={user} initialSource={params.get('source')} callbacksDue={dueToday} /></div>
            <div className="space-y-4"><CallbacksPanel items={callbacks} due={dueToday} onStart={startCallbacks} onDone={cbDone} onMove={cbMove} /><ResumeList sessions={sessions} onResume={resume} /></div>
          </div>
        )}
        {sid && !session && <p className="text-sm text-[var(--muted)]">Loading session…</p>}
        {session && finished && <Done session={session} onRequeue={async () => { try { const r = await axios.post(`${API}/api/calling/sessions/${sid}/requeue-skipped`, {}, { headers }); open(r.data); } catch (e) { toast.error(e.response?.data?.detail || 'Could not re-queue skipped'); } }} onNew={() => setParams({})} />}
        {session && !finished && item && (
          <div className="grid gap-4 lg:grid-cols-[280px_1fr_300px]">
            <div className="order-2 lg:order-1"><QueueList session={session} onGoto={(index) => post('goto', { index })} /></div>
            <div className="order-1 lg:order-2">
              <CallCard session={session} item={item} busy={busy}
                onDial={() => post('dial', { index: item.index })}
                onOutcome={async (outcome, note, callback_at) => { if (await post('outcome', { index: item.index, outcome, note, callback_at }, `Saved: ${session.outcomes[outcome]}${callback_at ? ' · callback booked' : ''}`)) refreshLobby(); }}
                onSkip={() => post('skip', { index: item.index })}
                onNote={(note) => post('note', { index: item.index, note }, 'Note saved')} />
            </div>
            <div className="order-3">
              <SidePanel session={session} item={item} headers={headers}
                onAdd={(key) => post('add', { keys: [key] }, 'Added to the end of this queue')}
                onPause={async () => { await post('pause', {}, 'Paused. Resume any time'); setParams({}); }}
                onFinish={() => post('finish', {}, 'Session finished')}
                onAppt={async (at) => { try { await axios.post(`${API}/api/calling/appointment`, { application_id: item.application_id, at }, { headers }); toast.success(at ? 'Appointment saved' : 'Appointment cleared'); load(); } catch { toast.error('Could not save appointment'); } }} />
            </div>
          </div>
        )}
      </div>
    </DwShell>
  );
}
